/**
 * Code-drawn stand-ins keyed by asset key.
 *
 * Every scene may reference any asset key freely. Keys that the manifest does not
 * resolve to a real SVG get a Phaser Graphics texture generated under the same key,
 * so swapping in Blender art later is a manifest-only change (CLAUDE.md).
 */
import type { Scene } from 'phaser';
import type { AssetGroupName, AssetKey, AssetManifest, PlaceholderSpec } from '../types';

/** Dark navy outline shared by all art (docs/ART_SPEC.md). */
export const OUTLINE = 0x1b2a41;

const SPRING_TEAL = 0x2ec4b6;
const JETPACK_ORANGE = 0xff8c42;
const ICE = 0xcfe8ff;
const BODY_WHITE = 0xf2f2f2;

const GROUP_NAMES: readonly AssetGroupName[] = [
  'animals',
  'platforms',
  'backgrounds',
  'powerups',
  'ui',
  'audio',
];

export function isAssetGroupName(value: string): value is AssetGroupName {
  return (GROUP_NAMES as readonly string[]).includes(value);
}

/** Splits "platforms/ice-floe/platform_1" into its group and remainder. */
export function splitAssetKey(key: AssetKey): { group: AssetGroupName | null; rest: string } {
  const slash = key.indexOf('/');
  const head = slash === -1 ? key : key.slice(0, slash);
  const rest = slash === -1 ? '' : key.slice(slash + 1);
  return { group: isAssetGroupName(head) ? head : null, rest };
}

/** True when the manifest resolves this key to a real file. */
export function manifestHasKey(manifest: AssetManifest | null, key: AssetKey): boolean {
  if (!manifest) return false;
  const { group, rest } = splitAssetKey(key);
  if (group === null || rest === '') return false;
  const entry = manifest.groups[group]?.[rest];
  return typeof entry?.file === 'string' && entry.file.length > 0;
}

/** Relative URL for a manifest-backed key, or null when it needs a placeholder. */
export function manifestFileFor(manifest: AssetManifest | null, key: AssetKey): string | null {
  if (manifest === null || !manifestHasKey(manifest, key)) return null;
  const { group, rest } = splitAssetKey(key);
  if (group === null) return null;
  const entry = manifest.groups[group]?.[rest];
  if (entry === undefined) return null;
  const base = manifest.basePath === '' ? 'assets' : manifest.basePath;
  const path = `${base}/${group}/${entry.file}`.replace(/\/{2,}/g, '/');
  return entry.hash === undefined || entry.hash === '' ? path : `${path}?v=${entry.hash}`;
}

/** Natural pixel size for a manifest-backed key, or null when it records none. */
export function manifestSizeFor(
  manifest: AssetManifest | null,
  key: AssetKey,
): { width: number; height: number } | null {
  if (manifest === null) return null;
  const { group, rest } = splitAssetKey(key);
  if (group === null) return null;
  const entry = manifest.groups[group]?.[rest];
  if (entry === undefined) return null;
  const { width, height } = entry;
  if (typeof width !== 'number' || typeof height !== 'number') return null;
  if (width <= 0 || height <= 0) return null;
  return { width, height };
}

/** Hash a key to a stable number so unknown placeholders stay distinguishable. */
export function hashKey(key: AssetKey): number {
  let h = 2166136261;
  for (let i = 0; i < key.length; i += 1) {
    h ^= key.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

/**
 * Pattern-based defaults, so a key nobody has specced still gets a sane shape.
 * Sizes match the spec table in CLAUDE.md.
 */
export function resolvePlaceholderSpec(key: AssetKey): PlaceholderSpec {
  const { group, rest } = splitAssetKey(key);
  const base = { outline: OUTLINE, outlineWidth: 5 };

  switch (group) {
    case 'animals':
      return rest.includes('_details')
        ? { ...base, width: 160, height: 160, shape: 'ellipse', fill: 0x1b2a41, accent: 0xffc145 }
        : {
            ...base,
            width: 160,
            height: 160,
            shape: 'ellipse',
            fill: BODY_WHITE,
            accent: 0xdcdcdc,
          };

    case 'platforms': {
      if (rest.startsWith('goal')) {
        return { ...base, width: 320, height: 200, shape: 'floe', fill: ICE, accent: 0xff4d6d };
      }
      if (rest.startsWith('start')) {
        return { ...base, width: 320, height: 56, shape: 'floe', fill: ICE, accent: 0x8ecae6 };
      }
      return { ...base, width: 256, height: 56, ...platformLook(rest) };
    }

    case 'backgrounds':
      return {
        ...base,
        width: 1280,
        height: 720,
        shape: 'backdrop',
        fill: 0x2b1b45,
        accent: 0xff7b54,
        outlineWidth: 0,
      };

    case 'powerups': {
      if (rest.startsWith('spring_coil')) {
        return {
          ...base,
          width: 64,
          height: 96,
          shape: 'capsule',
          fill: SPRING_TEAL,
          accent: 0xffffff,
        };
      }
      if (rest.startsWith('jetpack_worn')) {
        return {
          ...base,
          width: 80,
          height: 80,
          shape: 'roundRect',
          fill: JETPACK_ORANGE,
          accent: 0xffd166,
        };
      }
      const isJetpack = rest.includes('jetpack');
      return {
        ...base,
        width: 96,
        height: 96,
        shape: 'icon',
        fill: isJetpack ? JETPACK_ORANGE : SPRING_TEAL,
        accent: 0xffffff,
      };
    }

    case 'ui': {
      if (rest.startsWith('logo')) {
        return {
          ...base,
          width: 800,
          height: 240,
          shape: 'panel',
          fill: 0x8ecae6,
          accent: 0xffffff,
        };
      }
      if (rest.startsWith('star')) {
        return {
          ...base,
          width: 64,
          height: 64,
          shape: 'star',
          fill: rest.includes('empty') ? 0x44546b : 0xffc145,
          accent: 0xffffff,
        };
      }
      if (rest.startsWith('lock')) {
        return {
          ...base,
          width: 64,
          height: 64,
          shape: 'roundRect',
          fill: 0x8a99b3,
          accent: 0xffffff,
        };
      }
      return {
        ...base,
        width: 240,
        height: 72,
        shape: 'roundRect',
        fill: 0x2a4365,
        accent: 0x8ecae6,
      };
    }

    default: {
      const hue = hashKey(key) % 360;
      return {
        ...base,
        width: 128,
        height: 128,
        shape: 'roundRect',
        fill: hslToHex(hue, 55, 62),
        accent: 0xffffff,
      };
    }
  }
}

function platformLook(rest: string): Pick<PlaceholderSpec, 'shape' | 'fill' | 'accent'> {
  if (rest.startsWith('cloud')) return { shape: 'cloud', fill: 0xfdfdff, accent: 0xd6e4ff };
  if (rest.startsWith('marshmallow')) {
    return { shape: 'marshmallow', fill: 0xffe5ec, accent: 0xffc9de };
  }
  if (rest.startsWith('soccer-field')) {
    return { shape: 'soccerField', fill: 0x3fa65a, accent: 0xffffff };
  }
  // Original design: navy with action-green and silver. No team marks anywhere.
  if (rest.startsWith('game-day')) return { shape: 'gameDay', fill: 0x14213d, accent: 0x7ae582 };
  return { shape: 'floe', fill: ICE, accent: 0x8ecae6 };
}

export function hslToHex(h: number, s: number, l: number): number {
  const sat = s / 100;
  const lig = l / 100;
  const k = (n: number): number => (n + h / 30) % 12;
  const a = sat * Math.min(lig, 1 - lig);
  const f = (n: number): number =>
    lig - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));
  const to255 = (v: number): number => Math.round(255 * v);
  return (to255(f(0)) << 16) | (to255(f(8)) << 8) | to255(f(4));
}

export function lerpColor(a: number, b: number, t: number): number {
  const ar = (a >> 16) & 0xff;
  const ag = (a >> 8) & 0xff;
  const ab = a & 0xff;
  const br = (b >> 16) & 0xff;
  const bg = (b >> 8) & 0xff;
  const bb = b & 0xff;
  const r = Math.round(ar + (br - ar) * t);
  const g = Math.round(ag + (bg - ag) * t);
  const bl = Math.round(ab + (bb - ab) * t);
  return (r << 16) | (g << 8) | bl;
}

/* ------------------------------------------------------------------ drawing */

type Gfx = ReturnType<Scene['make']['graphics']>;

/** Generates the texture for one key if it does not exist yet. Returns true if drawn. */
export function ensurePlaceholderTexture(
  scene: Scene,
  key: AssetKey,
  override?: Partial<PlaceholderSpec>,
): boolean {
  if (scene.textures.exists(key)) return false;
  const spec: PlaceholderSpec = { ...resolvePlaceholderSpec(key), ...override };
  const gfx = scene.make.graphics({ x: 0, y: 0 }, false);
  drawSpec(gfx, spec, key);
  gfx.generateTexture(key, spec.width, spec.height);
  gfx.destroy();
  return true;
}

/** Generates placeholders for every key the manifest cannot satisfy. */
export function ensurePlaceholders(
  scene: Scene,
  keys: readonly AssetKey[],
  manifest: AssetManifest | null,
): AssetKey[] {
  const made: AssetKey[] = [];
  for (const key of keys) {
    if (manifestHasKey(manifest, key)) continue;
    if (ensurePlaceholderTexture(scene, key)) made.push(key);
  }
  return made;
}

function drawSpec(gfx: Gfx, spec: PlaceholderSpec, key: AssetKey): void {
  const { width: w, height: h, fill, accent, outline, outlineWidth: ow } = spec;
  gfx.fillStyle(fill, 1);
  gfx.lineStyle(ow, outline, 1);
  const inset = ow / 2;

  switch (spec.shape) {
    case 'rect':
      gfx.fillRect(0, 0, w, h);
      if (ow > 0) gfx.strokeRect(inset, inset, w - ow, h - ow);
      break;

    case 'roundRect': {
      const r = Math.min(w, h) * 0.22;
      gfx.fillRoundedRect(0, 0, w, h, r);
      if (ow > 0) gfx.strokeRoundedRect(inset, inset, w - ow, h - ow, r);
      gfx.fillStyle(accent, 0.9);
      gfx.fillRoundedRect(w * 0.12, h * 0.18, w * 0.76, h * 0.16, h * 0.08);
      break;
    }

    case 'ellipse':
      gfx.fillEllipse(w / 2, h / 2, w * 0.74, h * 0.9);
      if (ow > 0) gfx.strokeEllipse(w / 2, h / 2, w * 0.74, h * 0.9);
      gfx.fillStyle(accent, 1);
      gfx.fillEllipse(w / 2, h * 0.62, w * 0.42, h * 0.46);
      break;

    case 'floe': {
      const top = h * 0.3;
      gfx.beginPath();
      gfx.moveTo(w * 0.04, top);
      gfx.lineTo(w * 0.22, top * 0.35);
      gfx.lineTo(w * 0.8, top * 0.45);
      gfx.lineTo(w * 0.97, top);
      gfx.lineTo(w * 0.86, h * 0.96);
      gfx.lineTo(w * 0.14, h * 0.96);
      gfx.closePath();
      gfx.fillPath();
      if (ow > 0) gfx.strokePath();
      gfx.fillStyle(accent, 0.85);
      gfx.fillRect(w * 0.1, top * 0.9, w * 0.8, h * 0.1);
      break;
    }

    case 'cloud': {
      // Puffy, but with a flat top so the animal has somewhere to land.
      const top = h * 0.34;
      const puffs: ReadonlyArray<readonly [number, number, number]> = [
        [0.18, 0.22, 0.92],
        [0.38, 0.3, 1.12],
        [0.62, 0.28, 1.05],
        [0.84, 0.2, 0.86],
      ];
      for (const [cx, rx, ry] of puffs) {
        gfx.fillEllipse(w * cx, top + h * 0.18, w * rx, h * ry);
      }
      gfx.fillRect(w * 0.1, top, w * 0.8, h - top);

      gfx.fillStyle(accent, 0.75);
      gfx.fillRoundedRect(w * 0.12, top - h * 0.06, w * 0.76, h * 0.16, h * 0.08);
      break;
    }

    case 'capsule': {
      const r = Math.min(w, h) / 2;
      gfx.fillRoundedRect(0, 0, w, h, r);
      if (ow > 0) gfx.strokeRoundedRect(inset, inset, w - ow, h - ow, r);
      gfx.lineStyle(Math.max(2, ow * 0.6), accent, 0.8);
      for (let i = 1; i < 4; i += 1) {
        const x = (w / 4) * i;
        gfx.lineBetween(x, h * 0.18, x, h * 0.82);
      }
      break;
    }

    case 'marshmallow': {
      // A cylinder lying on its side: flat top, rounded caps, soft shading.
      const capW = h * 0.34;
      gfx.fillRoundedRect(0, 0, w, h, h / 2);
      if (ow > 0) gfx.strokeRoundedRect(inset, inset, w - ow, h - ow, h / 2);

      // The near cap reads as an ellipse on the left end.
      gfx.fillStyle(accent, 1);
      gfx.fillEllipse(capW / 2 + inset, h / 2, capW, h - ow);
      if (ow > 0) {
        gfx.lineStyle(ow, outline, 1);
        gfx.strokeEllipse(capW / 2 + inset, h / 2, capW, h - ow);
      }

      // A highlight band along the top of the roll.
      gfx.fillStyle(0xffffff, 0.5);
      gfx.fillRoundedRect(capW, h * 0.16, w - capW * 1.4, h * 0.2, h * 0.1);
      break;
    }

    case 'soccerField': {
      const r = 10;
      gfx.fillRoundedRect(0, 0, w, h, r);
      if (ow > 0) gfx.strokeRoundedRect(inset, inset, w - ow, h - ow, r);

      // Mown stripes.
      gfx.fillStyle(0x4cb866, 1);
      for (let i = 0; i < 4; i += 1) {
        gfx.fillRect(w * (i / 4) + w * 0.06, ow, w * 0.11, h - ow * 2);
      }

      // Touchlines, halfway line and centre circle.
      gfx.lineStyle(3, accent, 0.95);
      gfx.strokeRect(w * 0.08, h * 0.2, w * 0.84, h * 0.6);
      gfx.lineBetween(w / 2, h * 0.2, w / 2, h * 0.8);
      gfx.strokeCircle(w / 2, h / 2, h * 0.16);

      // A mini goal at each end.
      gfx.lineStyle(3, 0xffffff, 1);
      gfx.strokeRect(w * 0.02, h * 0.34, w * 0.06, h * 0.32);
      gfx.strokeRect(w * 0.92, h * 0.34, w * 0.06, h * 0.32);
      break;
    }

    case 'gameDay': {
      const r = 8;
      gfx.fillRoundedRect(0, 0, w, h, r);
      if (ow > 0) gfx.strokeRoundedRect(inset, inset, w - ow, h - ow, r);

      // Action-green and silver stripes, an original scheme - no team marks.
      gfx.fillStyle(accent, 1);
      gfx.fillRect(0, h * 0.26, w, h * 0.1);
      gfx.fillStyle(0xc9d1d9, 1);
      gfx.fillRect(0, h * 0.4, w, h * 0.06);

      // Yard lines across the deck.
      gfx.lineStyle(2, 0xffffff, 0.75);
      for (let i = 1; i < 6; i += 1) {
        const x = (w / 6) * i;
        gfx.lineBetween(x, h * 0.58, x, h * 0.9);
      }

      // A small football, with laces.
      const ballX = w * 0.5;
      const ballY = h * 0.74;
      gfx.fillStyle(0x7b3f1d, 1);
      gfx.fillEllipse(ballX, ballY, 34, 19);
      gfx.lineStyle(2, 0xffffff, 1);
      gfx.lineBetween(ballX - 10, ballY, ballX + 10, ballY);
      for (let i = -1; i <= 1; i += 1) {
        gfx.lineBetween(ballX + i * 5, ballY - 4, ballX + i * 5, ballY + 4);
      }
      break;
    }

    case 'star': {
      const cx = w / 2;
      const cy = h / 2;
      const outer = Math.min(w, h) * 0.46;
      const inner = outer * 0.46;
      gfx.beginPath();
      for (let i = 0; i < 10; i += 1) {
        const r = i % 2 === 0 ? outer : inner;
        const a = -Math.PI / 2 + (i * Math.PI) / 5;
        const px = cx + Math.cos(a) * r;
        const py = cy + Math.sin(a) * r;
        if (i === 0) gfx.moveTo(px, py);
        else gfx.lineTo(px, py);
      }
      gfx.closePath();
      gfx.fillPath();
      if (ow > 0) gfx.strokePath();
      break;
    }

    case 'icon': {
      const r = Math.min(w, h) * 0.44;
      gfx.fillCircle(w / 2, h / 2, r);
      if (ow > 0) gfx.strokeCircle(w / 2, h / 2, r);
      gfx.fillStyle(accent, 1);
      gfx.fillTriangle(w * 0.36, h * 0.66, w * 0.5, h * 0.3, w * 0.64, h * 0.66);
      break;
    }

    case 'panel': {
      const r = Math.min(w, h) * 0.16;
      gfx.fillRoundedRect(0, 0, w, h, r);
      if (ow > 0) gfx.strokeRoundedRect(inset, inset, w - ow, h - ow, r);
      gfx.fillStyle(accent, 0.55);
      gfx.fillRoundedRect(w * 0.06, h * 0.14, w * 0.88, h * 0.3, r * 0.6);
      break;
    }

    case 'backdrop': {
      // Cheap vertical ramp; real parallax art lands in Prompt 8.
      const bands = 24;
      for (let i = 0; i < bands; i += 1) {
        const t = i / (bands - 1);
        gfx.fillStyle(lerpColor(fill, accent, t), 1);
        gfx.fillRect(0, (h / bands) * i, w, h / bands + 1);
      }
      break;
    }
  }

  // Faint stripe so it is obvious on screen that the art is still a placeholder.
  gfx.fillStyle(0xffffff, 0.08);
  gfx.fillRect(0, h - Math.max(3, h * 0.03), (w * ((hashKey(key) % 60) + 20)) / 100, 3);
}
